import { useState } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { toggleButtonClasses } from '@/UI/AppToggleButtons';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import type { MouseEvent } from 'react';
import type { MenuAction } from '@/components/Menu/Actions';

interface AddItemButtonProps {
  show?: boolean;
  onClick?: () => void;
  text: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  menuItems?: MenuAction[];
}

export const AddItemButton = ({
  show = false,
  onClick,
  text,
  disabled = false,
  disabledTooltipText = '',
  menuItems,
}: AddItemButtonProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  if (!show) return null;

  const hasMenu = Boolean(menuItems?.length);

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(e.currentTarget);
  };

  const button = hasMenu ? (
    <AppToggleButton
      value={text}
      selected={menuAnchorEl !== null}
      onClick={handleMenuClick}
      disabled={disabled}
      aria-haspopup="menu"
      iconPrefix={<PlusIcon style={{ width: 16, height: 16 }} />}
      btnH={28}
      svgSize={16}
      sx={{
        gap: 0.5,
        border: 'none',
        color: token.text,
        '& svg': { color: token.text },
        '&:hover': { color: token.text },
        [`&.${toggleButtonClasses.disabled}`]: { border: 'none', color: token.textSecondary },
      }}
    >
      {text}
    </AppToggleButton>
  ) : (
    <ButtonContained
      mode="text"
      onClick={onClick}
      size="small"
      disabled={disabled}
      startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
    >
      {text}
    </ButtonContained>
  );

  const buttonWithTooltip = disabled ? (
    <Tooltip title={disabledTooltipText}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );

  return (
    <>
      {buttonWithTooltip}
      {hasMenu && (
        <AppContextMenu
          width="100px"
          open={menuAnchorEl !== null}
          onClose={() => setMenuAnchorEl(null)}
          anchorEl={menuAnchorEl}
          offset={{ top: '4px' }}
          items={menuItems}
        />
      )}
    </>
  );
};
